/**
 * What a founder is told when a bid is refused.
 *
 * `src/api/client.ts` deliberately does not reuse core's own message. Those are contract
 * text written for every caller, and some of them describe conditions ("Send
 * application/json", "Origin is not allowed") that a founder can do nothing about and
 * should never have to read. The **code** is the stable part of the contract, so the copy
 * is keyed off the code here instead (`contracts/API.md` § Seller bidding shape).
 *
 * Two rules govern the wording. Say what happened and what to do next — never apologise,
 * never be vague. And never invent an explanation the response does not support: an
 * unrecognised code gets the honest generic, not a guess.
 */
import { isNetworkError } from "../api/client";
import type { ApiError } from "../api/client";

const MESSAGES: Record<string, string> = {
  // Canon § 7: only an approved deal can bid, and approval is decided before bidding —
  // no amount makes an ineligible deal eligible.
  DEAL_NOT_ELIGIBLE_TO_BID:
    "This deal can't bid until Dealbid has approved it. Its review state is on your deals panel.",
  AUCTION_NOT_OPEN:
    "Bidding for tomorrow is closed. The next auction opens when tomorrow's board is finalised.",
  BID_BELOW_MINIMUM: "That's below the minimum for tomorrow's auction. Enter a higher amount.",
  BID_NOT_AN_INCREASE:
    "A standing bid can only be raised. Enter more than you have already bid, or leave it as it is.",
  INSUFFICIENT_PROMOTIONAL_CREDIT:
    "You don't have enough available promotional credit for that bid. Bid less, or wait for a standing bid to be released.",
  BID_CONFLICT: "Tomorrow's auction changed while this was being placed. Try again.",
  NOT_FOUND: "Dealbid no longer has this deal on your account. Reload your desk.",
  VALIDATION_FAILED: "Dealbid didn't accept that amount. Enter it in dollars, like 5 or 5.50.",
  MALFORMED_RESPONSE: "Dealbid didn't answer as expected. Your bid was not placed.",
};

/** The generic. Honest about the two things actually known: it failed, and retrying is fair. */
const GENERIC = "Dealbid couldn't place that bid. Try again in a moment.";

const OFFLINE =
  "Dealbid couldn't be reached, so the bid was not placed. Check your connection and try again.";

export function messageForBidError(error: ApiError): string {
  // A malformed body also carries a null status — the request completed and the answer was
  // unreadable — so the code is checked first. Telling a founder their connection dropped
  // when it did not would send them to restart a router that is working.
  if (error.code !== null) return MESSAGES[error.code] ?? GENERIC;
  return isNetworkError(error) ? OFFLINE : GENERIC;
}
